import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import U2000ActionBtn from '../../u2000Core/components/buttons/U2000ActionBtn';
import U2000Panel from '../../u2000Core/components/layout/U2000Panel';
import { NS } from '../U3207';
import type {
  U3207ConversationRow,
  U3207ConversationsDrilldown,
  U3207GroupDrilldown,
  U3207ProviderDrilldown,
  U3207UserDrilldown,
} from '../U3207Service';
import {
  u3207BuildBreadcrumbLabels,
  type U3207BreadcrumbItem,
} from '../utils/u3207Breadcrumb';
import {
  u3207FormatTokenCount,
  u3207FormatUsagePercent,
  u3207FormatUserLabel,
  u3207FormatCodeNameLabel,
} from '../utils/u3207Formatters';
import U3207QuotaBar from './U3207QuotaBar';

interface U3207DrillPanelProps {
  open: boolean;
  trail: U3207BreadcrumbItem[];
  loading: boolean;
  providerDrill: U3207ProviderDrilldown | null;
  groupDrill: U3207GroupDrilldown | null;
  userDrill: U3207UserDrilldown | null;
  conversationsDrill: U3207ConversationsDrilldown | null;
  /** Provider du parcours (1er niveau) — filtre d'affichage détail. */
  scopedProviderId?: string;
  onClose: () => void;
  onBreadcrumbClick: (trailIndex: number) => void;
  onSelectGroup: (groupId: string, label: string, parentProviderId?: string) => void;
  onSelectGroupUsers: (groupId: string, label: string) => void;
  onSelectUser: (userId: string, label: string) => void;
  onSelectUserConversations: (userId: string, label: string) => void;
}

function ConversationList({
  rows,
  locale,
  onOpen,
}: {
  rows: U3207ConversationRow[];
  locale: string;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation(NS);
  if (!rows.length) {
    return <p className="text-sm text-muted-foreground">{t('empty')}</p>;
  }
  return (
    <ul className="space-y-2">
      {rows.map((row) => (
        <li key={row.conversationId}>
          <U2000ActionBtn
            variant="secondary"
            className="w-full! justify-start! px-3! py-2! text-left"
            handleClick={() => onOpen(row.conversationId)}
            label={
              <span className="flex min-w-0 flex-col items-start gap-0.5">
                <span className="font-medium">
                  {row.title?.trim() || t('conversationUntitled')}
                </span>
                <span className="text-xs font-normal text-muted-foreground">
                  {row.providerCode ? `${row.providerCode} · ` : ''}
                  {t('kpi.tokens')}: {u3207FormatTokenCount(row.tokens, locale)}
                </span>
              </span>
            }
          />
        </li>
      ))}
    </ul>
  );
}

function U3207DrillPanel({
  open,
  trail,
  loading,
  providerDrill,
  groupDrill,
  userDrill,
  conversationsDrill,
  scopedProviderId,
  onClose,
  onBreadcrumbClick,
  onSelectGroup,
  onSelectGroupUsers,
  onSelectUser,
  onSelectUserConversations,
}: U3207DrillPanelProps) {
  const { t, i18n } = useTranslation(NS);
  const navigate = useNavigate();
  const locale = i18n.language;
  const labels = u3207BuildBreadcrumbLabels(t('breadcrumb.org'), trail);
  const current = trail[trail.length - 1];

  if (!open) return null;

  const openConversation = (conversationId: string) => {
    navigate(`/U3204/${conversationId}`);
  };

  return (
    <aside className="w-full lg:sticky lg:top-4 lg:h-[calc(100vh-6rem)] lg:w-[min(40rem,48%)] lg:min-w-md lg:shrink-0">
      <U2000Panel title={t('drill.title')} className="flex h-full flex-col!" variant="elevated">
        <div className="mb-3 space-y-2 border-b border-border pb-3">
          <div className="flex justify-end">
            <U2000ActionBtn
              variant="secondary"
              label={t('drill.close')}
              handleClick={onClose}
              className="shrink-0! px-2! py-1! text-xs"
            />
          </div>
          <nav
            className="flex w-full flex-wrap items-center gap-x-1 gap-y-1 text-xs leading-relaxed"
            aria-label="breadcrumb"
          >
            {labels.map((label, index) => {
              const isLast = index === labels.length - 1;
              const trailIndex = index - 1;
              return (
                <span key={`${index}-${label}`} className="inline-flex max-w-full items-center gap-1">
                  {index > 0 ? (
                    <span className="shrink-0 text-muted-foreground" aria-hidden>
                      ›
                    </span>
                  ) : null}
                  {isLast || index === 0 ? (
                    <span
                      className={`min-w-0 wrap-break-word ${
                        isLast ? 'font-semibold text-foreground' : 'text-muted-foreground'
                      }`}
                    >
                      {label}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="min-w-0 wrap-break-word text-left text-primary underline-offset-2 hover:underline"
                      onClick={() => onBreadcrumbClick(trailIndex)}
                    >
                      {label}
                    </button>
                  )}
                </span>
              );
            })}
          </nav>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
          {loading ? <p className="text-sm text-muted-foreground">{t('loading')}</p> : null}

          {!loading && current?.level === 'provider' && providerDrill ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t('kpi.tokens')}: {u3207FormatTokenCount(providerDrill.tokens, locale)} ·{' '}
                {t('kpi.users')}: {providerDrill.distinctUsers} · {t('kpi.conversations')}:{' '}
                {providerDrill.distinctConversations}
              </p>
              <ul className="space-y-2">
                {providerDrill.groups.map((g) => (
                  <li key={g.groupId} className="space-y-1 rounded-sm border border-border p-3">
                    <U2000ActionBtn
                      variant="secondary"
                      className="w-full! justify-start! border-0! bg-transparent! px-0! py-0! shadow-none!"
                      handleClick={() =>
                        onSelectGroup(
                          g.groupId,
                          u3207FormatCodeNameLabel(g.code, g.name),
                          providerDrill.providerId,
                        )
                      }
                      label={
                        <span className="flex min-w-0 flex-col items-start gap-1 text-left">
                          <span className="text-sm font-medium text-foreground">
                            {u3207FormatCodeNameLabel(g.code, g.name)}
                          </span>
                          <span className="text-xs font-normal text-muted-foreground">
                            {t('kpi.tokens')}: {u3207FormatTokenCount(g.tokens, locale)} ·{' '}
                            {t('kpi.users')}: {g.distinctUsers} · {t('kpi.conversations')}:{' '}
                            {g.distinctConversations}
                            {g.usagePercent != null
                              ? ` · ${t('kpi.quota')}: ${u3207FormatUsagePercent(g.usagePercent, locale)}`
                              : ''}
                          </span>
                          <U3207QuotaBar usagePercent={g.usagePercent} />
                        </span>
                      }
                    />
                    <U2000ActionBtn
                      variant="secondary"
                      className="px-2! py-1! text-xs"
                      label={t('drill.viewUsers')}
                      handleClick={() =>
                        onSelectGroupUsers(g.groupId, u3207FormatCodeNameLabel(g.code, g.name))
                      }
                    />
                  </li>
                ))}
              </ul>
              {!providerDrill.groups.length ? (
                <p className="text-sm text-muted-foreground">{t('empty')}</p>
              ) : null}
            </div>
          ) : null}

          {!loading && (current?.level === 'group' || current?.level === 'groupUsers') && groupDrill ? (
            <div className="space-y-4">
              {current.level === 'group' ? (
                <ul className="space-y-2">
                  {groupDrill.providers
                    .filter((p) => !scopedProviderId || p.providerId === scopedProviderId)
                    .map((p) => (
                    <li key={p.providerId} className="rounded-sm border border-border p-3">
                      <p className="text-sm font-medium">
                        {u3207FormatCodeNameLabel(p.code, p.name)}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t('kpi.tokens')}: {u3207FormatTokenCount(p.tokens, locale)}
                        {p.usagePercent != null
                          ? ` · ${t('kpi.quota')}: ${u3207FormatUsagePercent(p.usagePercent, locale)}`
                          : ` · ${t('kpi.quota')}: ${t('kpi.unlimited')}`}
                      </p>
                      <U3207QuotaBar usagePercent={p.usagePercent} />
                    </li>
                  ))}
                </ul>
              ) : null}
              {current.level === 'group' ? (
                <U2000ActionBtn
                  variant="secondary"
                  className="px-3! py-1.5! text-xs"
                  label={t('drill.viewUsers')}
                  handleClick={() =>
                    onSelectGroupUsers(
                      groupDrill.groupId,
                      u3207FormatCodeNameLabel(groupDrill.code, groupDrill.name),
                    )
                  }
                />
              ) : null}
              {current.level === 'groupUsers' && groupDrill.users ? (
                <ul className="space-y-2">
                  {groupDrill.users.map((u) => {
                    const label = u3207FormatUserLabel(u);
                    return (
                      <li key={u.userId}>
                        <U2000ActionBtn
                          variant="secondary"
                          className="w-full! justify-start! px-3! py-2! text-left"
                          handleClick={() => onSelectUser(u.userId, label)}
                          label={
                            <span className="flex min-w-0 flex-col items-start gap-0.5">
                              <span className="font-medium">{label}</span>
                              <span className="text-xs font-normal text-muted-foreground">
                                {t('kpi.tokens')}: {u3207FormatTokenCount(u.tokens, locale)} ·{' '}
                                {t('kpi.conversations')}: {u.conversationCount}
                              </span>
                            </span>
                          }
                        />
                      </li>
                    );
                  })}
                  {!groupDrill.users.length ? (
                    <p className="text-sm text-muted-foreground">{t('empty')}</p>
                  ) : null}
                </ul>
              ) : null}
            </div>
          ) : null}

          {!loading && current?.level === 'user' && userDrill ? (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-foreground">
                {u3207FormatUserLabel(userDrill)}
              </h4>
              <ul className="space-y-2">
                {userDrill.providers
                  .filter((p) => !scopedProviderId || p.providerId === scopedProviderId)
                  .map((p) => (
                  <li key={p.providerId} className="rounded-sm border border-border p-3">
                    <p className="text-sm font-medium">
                      {u3207FormatCodeNameLabel(p.code, p.name)}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('kpi.tokens')}: {u3207FormatTokenCount(p.tokens, locale)}
                      {p.usagePercent != null
                        ? ` · ${t('kpi.quota')}: ${u3207FormatUsagePercent(p.usagePercent, locale)}`
                        : ` · ${t('kpi.quota')}: ${t('kpi.unlimited')}`}
                    </p>
                    <U3207QuotaBar usagePercent={p.usagePercent} />
                  </li>
                ))}
              </ul>
              <div>
                <h5 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                  {t('tile.conversations')}
                </h5>
                <ConversationList
                  rows={userDrill.conversations.filter(
                    (c) => !scopedProviderId || c.providerId === scopedProviderId,
                  )}
                  locale={locale}
                  onOpen={openConversation}
                />
              </div>
            </div>
          ) : null}

          {!loading && current?.level === 'conversations' && conversationsDrill?.users ? (
            <ul className="space-y-2">
              {conversationsDrill.users.map((u) => {
                const label = u3207FormatUserLabel(u);
                return (
                  <li key={u.userId}>
                    <U2000ActionBtn
                      variant="secondary"
                      className="w-full! justify-start! px-3! py-2! text-left"
                      handleClick={() => onSelectUserConversations(u.userId, label)}
                      label={
                        <span className="flex min-w-0 flex-col items-start gap-0.5">
                          <span className="font-medium">{label}</span>
                          <span className="text-xs font-normal text-muted-foreground">
                            {t('kpi.conversations')}: {u.conversationCount} · {t('kpi.tokens')}:{' '}
                            {u3207FormatTokenCount(u.totalTokens, locale)}
                          </span>
                        </span>
                      }
                    />
                  </li>
                );
              })}
              {!conversationsDrill.users.length ? (
                <p className="text-sm text-muted-foreground">{t('empty')}</p>
              ) : null}
            </ul>
          ) : null}

          {!loading &&
          (current?.level === 'userConversations' ||
            (current?.level === 'conversations' && conversationsDrill?.conversations)) &&
          conversationsDrill?.conversations ? (
            <ConversationList
              rows={conversationsDrill.conversations}
              locale={locale}
              onOpen={openConversation}
            />
          ) : null}
        </div>
      </U2000Panel>
    </aside>
  );
}

export default U3207DrillPanel;
